import { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Portal from '@radix-ui/react-portal';
import { computeRevealCost } from './keyContactsCostUtils';
import { contactKey } from './keyContactsUtils';
import EnrichmentCreditCounter from '../../../common/EnrichmentCreditCounter';
import KeyContactsMobileSheet from './KeyContactsMobileSheet';
import { ICON_BUTTON_ICONS } from '../../../buttons/IconButton';

/**
 * Action bar for the Key Contacts tab.
 *
 * Desktop: [Discover] [master checkbox] … [Reveal selected (N) ▾] [credits] with
 * the reveal options in an anchored dropdown.
 *
 * Mobile (TD-2485): a persistent bottom action bar (thumb zone) — a slim strip
 * (master checkbox + credits) over a two-button row (Discover + Reveal); the
 * reveal options open in a modal bottom sheet (KeyContactsMobileSheet) with the
 * "Skip previously enriched" toggle split out on top and the three options as
 * large one-tap option cards.
 *
 * Master checkbox click semantics (per Phase 3b spec):
 *   - 0 selected       → onSelectAll()
 *   - 1..N-1 selected  → onSelectAll()  (indeterminate → all; spec-driven)
 *   - all selected     → onDeselectAll()
 */

const pluralise = (n, noun) => (n === 1 ? `${n} ${noun}` : `${n} ${noun}s`);
const creditLabel = (n) => pluralise(n, 'credit');

// Window in which a repeated reveal activation is treated as an accidental
// double-tap rather than a second intentional spend.
const REVEAL_REPEAT_GUARD_MS = 700;

const SKIP_DESCRIPTION_TEXT =
  'When enabled, contacts already enriched for the chosen field are excluded from the credit cost.';

// Reveal-trigger labels as flat if/else (not nested ternaries) — clearer and
// keeps KeyContactsActionBar's cognitive complexity down.
const getMobileRevealLabel = (isRevealing, selectedCount, bothCost) => {
  if (isRevealing) return 'Revealing…';
  if (selectedCount === 0) return 'Reveal selected';
  return `Reveal (${selectedCount}) · ${bothCost} cr`;
};

const getDesktopRevealLabel = (isRevealing, revealDisabled, selectedCount) => {
  if (isRevealing) return 'Revealing…';
  if (revealDisabled) return 'Reveal selected';
  return `Reveal selected (${selectedCount})`;
};

/**
 * One reveal option in the mobile sheet: icon + label + one-line hint + live
 * credit cost. Tapping it reveals immediately (one tap, no confirm — the cost is
 * on the card).
 */
const RevealOptionCard = ({
  icon,
  label,
  hint,
  cost,
  recommended = false,
  disabled = false,
  onClick
}) => (
  <button
    type="button"
    className={`kc-reveal-card${recommended ? ' is-recommended' : ''}`}
    onClick={onClick}
    disabled={disabled}
    title={disabled ? 'All selected contacts already have this field enriched' : undefined}
  >
    {icon ? <img src={icon} alt="" aria-hidden="true" className="kc-reveal-card-icon" /> : null}
    <span className="kc-reveal-card-text">
      <span className="kc-reveal-card-label">{label}</span>
      {hint ? (
        <span className={`kc-reveal-card-hint${recommended ? ' is-recommended' : ''}`}>{hint}</span>
      ) : null}
    </span>
    <span className="kc-reveal-card-cost">{creditLabel(cost)}</span>
  </button>
);

RevealOptionCard.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  cost: PropTypes.number.isRequired,
  recommended: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

/**
 * One reveal option in the desktop dropdown. Extracted so the zero-cost gating
 * lives here rather than inflating KeyContactsActionBar's complexity.
 */
const RevealDropdownOption = ({ label, cost, onClick }) => {
  const zeroCost = cost === 0;
  return (
    <button
      type="button"
      className="key-contacts-action-bar-dropdown-option"
      onClick={onClick}
      disabled={zeroCost}
      title={zeroCost ? 'All selected contacts already have this field enriched' : undefined}
    >
      {`${label}: ${creditLabel(cost)}`}
    </button>
  );
};

RevealDropdownOption.propTypes = {
  label: PropTypes.string.isRequired,
  cost: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired
};

const KeyContactsActionBar = ({
  contacts = [],
  // TD-2549: full billable universe for the cost preview — a superset of the
  // visible `contacts` that includes selected contacts the active filter hides.
  // Defaults to `contacts` so callers that don't split visible vs. billable keep
  // the prior behaviour.
  allContacts = contacts,
  selectedIds,
  onSelectAll,
  onDeselectAll,
  onReveal,
  isRevealing = false,
  enrichment,
  isLoading,
  discoverSlot,
  // TD-2485: 'mobile' switches to the sticky bottom bar + reveal sheet.
  layout = 'desktop'
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [skipPreviouslyEnriched, setSkipPreviouslyEnriched] = useState(true);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const lastRevealAtRef = useRef(0);
  const skipDescriptionId = useId();

  const isMobile = layout === 'mobile';
  const totalCount = contacts.length;
  const selectedCount = selectedIds?.size ?? 0;
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && !allSelected;

  // TD-2549: cost the FULL selection, not just the visible rows. Selection is
  // kept independent of the filters, and a reveal bills every selected contact
  // (KeyContactsTab.handleBulkReveal resolves them from the unfiltered lists), so
  // costing only `contacts` (the visible set) understates the spend whenever the
  // filter hides a selected contact. `allContacts` is that full billable set.
  const selectedContacts = useMemo(
    () => allContacts.filter((c) => selectedIds?.has(contactKey(c))),
    [allContacts, selectedIds]
  );

  const costs = useMemo(
    () => computeRevealCost(selectedContacts, skipPreviouslyEnriched),
    [selectedContacts, skipPreviouslyEnriched]
  );

  // Desktop only: the anchored dropdown needs outside-click + Escape. On mobile
  // the reveal sheet's useModalA11y owns dismissal (and the sheet is top-layer,
  // so a click inside it would read as "outside" this container).
  useEffect(() => {
    if (!isDropdownOpen || isMobile) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownOpen, isMobile]);

  const handleMasterClick = () => {
    if (allSelected) onDeselectAll();
    else onSelectAll();
  };

  const handleRevealOption = (field) => {
    // Credit spend is one tap with no confirm step, so a double-tap (iOS still
    // emits two clicks) must not bill twice. `isRevealing` alone can't carry
    // this: it only arrives from the parent a render later, and every pre-flight
    // abort in handleBulkReveal (nothing eligible, insufficient credits, …)
    // returns without ever setting it. A timestamp window covers the gap and,
    // unlike a boolean latch, cannot stick and lock the user out.
    const now = Date.now();
    if (isRevealing || now - lastRevealAtRef.current < REVEAL_REPEAT_GUARD_MS) return;
    lastRevealAtRef.current = now;
    onReveal(field, skipPreviouslyEnriched);
    setIsDropdownOpen(false);
  };

  const revealDisabled = selectedCount === 0 || isRevealing;
  const masterLabel = allSelected ? 'Deselect all' : 'Select all';
  const revealStateClass = revealDisabled ? 'is-disabled' : 'is-active';

  // Shared reveal options (field + label + live cost); mobile adds icon + hint.
  const revealOptions = [
    {
      field: 'email',
      label: 'Find email',
      hint: 'Lowest cost',
      icon: ICON_BUTTON_ICONS.ENVELOPE,
      cost: costs.email
    },
    {
      field: 'phone',
      label: 'Find mobile',
      hint: 'Direct dial',
      icon: ICON_BUTTON_ICONS.TELEPHONE,
      cost: costs.phone
    },
    {
      field: 'both',
      label: 'Find email + mobile',
      hint: 'Most complete',
      icon: ICON_BUTTON_ICONS.ENVELOPE,
      cost: costs.both,
      recommended: true
    }
  ];

  // ---- Shared nodes (identical markup across both layouts) ----
  const masterCheckbox = (
    <label className="key-contacts-action-bar-master">
      <span className="key-contacts-action-bar-master-box">
        <input
          type="checkbox"
          checked={allSelected}
          ref={(input) => {
            if (input) input.indeterminate = isIndeterminate;
          }}
          onChange={handleMasterClick}
        />
      </span>
      <span className="key-contacts-action-bar-master-label">{masterLabel}</span>
    </label>
  );

  const creditCounter = (
    <EnrichmentCreditCounter variant="row" enrichment={enrichment} isLoading={isLoading} />
  );

  const skipToggle = (
    <label className="key-contacts-action-bar-skip-toggle">
      <input
        type="checkbox"
        checked={skipPreviouslyEnriched}
        onChange={(e) => setSkipPreviouslyEnriched(e.target.checked)}
        aria-describedby={skipDescriptionId}
        title={SKIP_DESCRIPTION_TEXT}
      />
      <span>Skip previously enriched</span>
      <span id={skipDescriptionId} className="key-contacts-action-bar-sr-only">
        {SKIP_DESCRIPTION_TEXT}
      </span>
    </label>
  );

  // ---- Mobile: sticky bottom bar + reveal sheet ----
  if (isMobile) {
    const revealSheetTitle =
      selectedCount > 0 ? `Reveal selected (${selectedCount})` : 'Reveal selected';

    return (
      <>
        {/* TD-2485: portal the fixed bar to <body>. `.key-contacts-wrap` carries a
            transform (identity matrix), which would otherwise be the containing
            block for position:fixed and pin the bar to the bottom of the tall
            content instead of the viewport. */}
        <Portal.Root>
          <div className="key-contacts-sticky-bar" ref={containerRef}>
            <div className="key-contacts-sticky-bar-inner">
              <div className="key-contacts-sticky-bar-strip">
                {masterCheckbox}
                {creditCounter}
              </div>
              <div className="key-contacts-sticky-bar-actions">
                {discoverSlot}
                <button
                  ref={triggerRef}
                  type="button"
                  className={`key-contacts-action-bar-reveal-trigger kc-sticky-reveal ${revealStateClass}`}
                  onClick={() => setIsDropdownOpen(true)}
                  disabled={revealDisabled}
                  aria-haspopup="dialog"
                  aria-expanded={isDropdownOpen}
                >
                  <span className="key-contacts-action-bar-reveal-label">
                    {getMobileRevealLabel(isRevealing, selectedCount, costs.both)}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </Portal.Root>

        <KeyContactsMobileSheet
          isOpen={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
          title={revealSheetTitle}
        >
          {/* AC10: Skip toggle separated at the top, above a divider. */}
          <div className="kc-reveal-sheet-skip">{skipToggle}</div>
          <fieldset className="kc-reveal-cards" aria-label="Reveal options">
            {revealOptions.map((opt) => (
              <RevealOptionCard
                key={opt.field}
                icon={opt.icon}
                label={opt.label}
                hint={opt.hint}
                cost={opt.cost}
                recommended={opt.recommended}
                disabled={opt.cost === 0 || isRevealing}
                onClick={() => handleRevealOption(opt.field)}
              />
            ))}
          </fieldset>
        </KeyContactsMobileSheet>
      </>
    );
  }

  // ---- Desktop: inline row + anchored dropdown (unchanged behaviour) ----
  return (
    <div className="key-contacts-action-bar" ref={containerRef}>
      {discoverSlot}
      <div className="key-contacts-action-bar-select-reveal">
        {masterCheckbox}
        <div className="key-contacts-action-bar-reveal-wrap">
          <button
            ref={triggerRef}
            type="button"
            className={`key-contacts-action-bar-reveal-trigger ${revealStateClass} ${
              isDropdownOpen ? 'is-open' : ''
            }`}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            disabled={revealDisabled}
            aria-expanded={isDropdownOpen}
            title={skipPreviouslyEnriched ? 'Previously-enriched contacts excluded' : undefined}
          >
            <span className="key-contacts-action-bar-reveal-label">
              {getDesktopRevealLabel(isRevealing, revealDisabled, selectedCount)}
            </span>
            {skipPreviouslyEnriched && (
              <span
                className="key-contacts-action-bar-reveal-skip-hint"
                aria-label="previously enriched excluded"
              >
                {' '}
                (skip on)
              </span>
            )}
            <span className="key-contacts-action-bar-reveal-caret" aria-hidden="true" />
          </button>

          {isDropdownOpen && (
            <fieldset className="key-contacts-action-bar-dropdown" aria-label="Reveal options">
              {revealOptions.map(({ field, label, cost }) => (
                <RevealDropdownOption
                  key={field}
                  label={label}
                  cost={cost}
                  onClick={() => handleRevealOption(field)}
                />
              ))}
              {skipToggle}
            </fieldset>
          )}
        </div>
      </div>

      {creditCounter}
    </div>
  );
};

KeyContactsActionBar.propTypes = {
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      person_crefo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      enrichment_status_email: PropTypes.string,
      enrichment_status_phone: PropTypes.string
    })
  ).isRequired,
  // TD-2549: full billable universe for the cost preview (superset of the
  // visible `contacts`). Defaults to `contacts` when a caller doesn't split them.
  allContacts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      person_crefo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      enrichment_status_email: PropTypes.string,
      enrichment_status_phone: PropTypes.string
    })
  ),
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func.isRequired,
  onReveal: PropTypes.func.isRequired,
  isRevealing: PropTypes.bool,
  enrichment: PropTypes.shape({
    used: PropTypes.number,
    limit: PropTypes.number,
    remaining: PropTypes.number,
    color: PropTypes.string,
    limitReached: PropTypes.bool,
    periodEnd: PropTypes.string
  }),
  isLoading: PropTypes.bool,
  discoverSlot: PropTypes.node,
  layout: PropTypes.oneOf(['desktop', 'mobile', 'tablet'])
};

export default KeyContactsActionBar;
